import { PropertySubsetValidator } from 'types/api';
import { HandlingRequestSchemaLite } from 'types/clientV2/stock';
import {
  StockLocationPurpose,
  StockLocationSchemaLite,
} from 'types/clientV2/stockLocations';
import { HandlingRequestType, StockLocation } from 'types/inventory';

export const KIT_REQUEST_PURPOSES = [
  StockLocationPurpose.COUNTING,
  StockLocationPurpose.SHIPPING,
  StockLocationPurpose.SPLITTING,
  StockLocationPurpose.SPLICING,
  StockLocationPurpose.REELING,
  StockLocationPurpose.INSPECTION,
  StockLocationPurpose.PICKING,
];

export const ORG_TEAM_PURPOSES = [
  StockLocationPurpose.PUTTING,
  StockLocationPurpose.STORAGE,
];

export const inspectionRequestTypes = [
  HandlingRequestType.INSPECTION_PHOTO,
  HandlingRequestType.INSPECTION_PHOTO_SAMPLE,
  HandlingRequestType.INSPECTION_PHOTO_FULL,
  HandlingRequestType.INSPECTION_DIMENSIONAL,
  HandlingRequestType.INSPECTION_LCR,
  HandlingRequestType.INSPECTION_SURFACE,
  HandlingRequestType.INSPECTION_DECAP,
  HandlingRequestType.INSPECTION_3RD_PARTY,
];

export const splicingRequestTypes = [
  HandlingRequestType.SPLICE,
  HandlingRequestType.COMBINE,
];

export const freeLocationPurposes = [
  StockLocationPurpose.STORAGE,
  StockLocationPurpose.PUTTING,
  StockLocationPurpose.RECEIVING,
];

export const purposeToRequestTypes: Partial<
  Record<StockLocationPurpose, HandlingRequestType[]>
> = {
  [StockLocationPurpose.SPLITTING]: [HandlingRequestType.SPLIT],
  [StockLocationPurpose.SPLICING]: splicingRequestTypes,
  [StockLocationPurpose.REELING]: [HandlingRequestType.REEL],
  [StockLocationPurpose.BAKING]: [HandlingRequestType.MSL_RESET],
  [StockLocationPurpose.SHIPPING]: [HandlingRequestType.KIT],
  [StockLocationPurpose.INSPECTION]: inspectionRequestTypes,
  [StockLocationPurpose.COUNTING]: [HandlingRequestType.CHECK_QUANT],
};

export const requestTypeToPurpose: Partial<
  Record<HandlingRequestType, StockLocationPurpose>
> = Object.entries(purposeToRequestTypes).reduce(
  (acc, [purpose, requestTypes]) => {
    requestTypes.forEach((requestType) => {
      acc[requestType] = purpose as StockLocationPurpose;
    });
    return acc;
  },
  {} as Partial<Record<HandlingRequestType, StockLocationPurpose>>
);

export const handlingRequestExecutionOrder = [
  HandlingRequestType.CHECK_QUANT,
  HandlingRequestType.MSL_RESET,
  HandlingRequestType.SPLIT,
  HandlingRequestType.SPLICE,
  HandlingRequestType.COMBINE,
  HandlingRequestType.REEL,
  HandlingRequestType.REPACKAGE,
  HandlingRequestType.KIT,
];

export const getNextHandlingRequest = <
  T extends { requestType: HandlingRequestType }
>(
  handlingRequests: T[]
): T | null => {
  if (handlingRequests.length === 0) return null;
  const sortedRequests = [...handlingRequests].sort(
    (a, b) =>
      handlingRequestExecutionOrder.indexOf(a.requestType) -
      handlingRequestExecutionOrder.indexOf(b.requestType)
  );
  return sortedRequests[0] || null;
};

export enum MismatchReason {
  PURPOSE = 'purpose',
  KIT_REQUEST = 'kit_request',
}

// Function overloading to support returnMismatchDetails parameter
export function matchHandlingRequestToLocationAssignment(
  handlingRequest: PropertySubsetValidator<
    HandlingRequestSchemaLite,
    'requestType' | 'kitRequestIdViaLot'
  > | null,
  location: StockLocationSchemaLite | StockLocation | null,
  returnMismatchDetails: true
): { matched: boolean; mismatches: MismatchReason[] };

export function matchHandlingRequestToLocationAssignment(
  handlingRequest: PropertySubsetValidator<
    HandlingRequestSchemaLite,
    'requestType' | 'kitRequestIdViaLot'
  > | null,
  location: StockLocationSchemaLite | StockLocation | null,
  returnMismatchDetails?: false
): boolean;

export function matchHandlingRequestToLocationAssignment(
  handlingRequest: PropertySubsetValidator<
    HandlingRequestSchemaLite,
    'requestType' | 'kitRequestIdViaLot'
  > | null,
  location: StockLocationSchemaLite | StockLocation | null,
  returnMismatchDetails = false
): boolean | { matched: boolean; mismatches: MismatchReason[] } {
  if (!location?.purpose) {
    if (returnMismatchDetails) {
      return {
        matched: false,
        mismatches: [MismatchReason.PURPOSE],
      };
    }
    return false;
  }

  // If no handling request, only allow specific "free" purposes
  if (!handlingRequest) {
    const isAllowed = freeLocationPurposes.includes(location.purpose);

    if (returnMismatchDetails) {
      return {
        matched: isAllowed,
        mismatches: isAllowed ? [] : [MismatchReason.PURPOSE],
      };
    }
    return isAllowed;
  }

  const mismatches: MismatchReason[] = [];

  // Check kit request mismatches
  const hasLocationKitId = Boolean(location.kitRequest);
  const hasStockLotKitId = Boolean(handlingRequest.kitRequestIdViaLot);

  if (
    hasLocationKitId !== hasStockLotKitId ||
    (hasLocationKitId &&
      hasStockLotKitId &&
      location.kitRequest !== handlingRequest.kitRequestIdViaLot)
  ) {
    mismatches.push(MismatchReason.KIT_REQUEST);
  }

  // Check purpose mismatches using the unified approach
  const expectedRequestTypes = purposeToRequestTypes[location.purpose];
  const isPurposeValid =
    expectedRequestTypes?.includes(handlingRequest.requestType) ?? false;

  if (!isPurposeValid) {
    mismatches.push(MismatchReason.PURPOSE);
  }

  if (returnMismatchDetails) {
    return { matched: mismatches.length === 0, mismatches };
  }
  return mismatches.length === 0;
}

export const matchHandlingRequestsToLocationAssignment = (
  handlingRequests:
    | PropertySubsetValidator<
        HandlingRequestSchemaLite,
        'requestType' | 'kitRequestIdViaLot'
      >[]
    | null,
  location: StockLocationSchemaLite | StockLocation | null
) => {
  if (!handlingRequests?.length || !location) return false;

  return handlingRequests.some((request) =>
    matchHandlingRequestToLocationAssignment(request, location)
  );
};
